import React, { createContext, useContext, useMemo } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useAuth, useUser } from "../ClerkAuthentication/clerkReact";
import ClerkAuthProvider from "../ClerkAuthentication/ClerkAuthProvider";
import { getConfigValue } from "../../config/runtimeConfig";

export const AUTH_PROVIDERS = {
  AUTH0: "AUTH0",
  CLERK: "CLERK",
};

export const getAuthProvider = () => {
  const provider = getConfigValue(
    "REACT_APP_AUTH_PROVIDER",
    process.env.REACT_APP_AUTH_PROVIDER,
    AUTH_PROVIDERS.AUTH0
  );
  return provider.toUpperCase();
};

const normalizeAuth0User = (user) => {
  if (!user) return null;
  return {
    id: user.sub || user.user_id || null,
    email: user.email || null,
    name: user.name || user.nickname || user.email || "User",
    avatarUrl: user.picture || null,
  };
};

const normalizeClerkUser = (user) => {
  if (!user) return null;
  const emailAddress = user.primaryEmailAddress?.emailAddress || null;
  return {
    id: user.id || null,
    email: emailAddress,
    name: user.fullName || user.firstName || emailAddress || "User",
    avatarUrl: user.imageUrl || null,
  };
};

const AuthContext = createContext({
  provider: AUTH_PROVIDERS.AUTH0,
  isLoading: false,
  isAuthenticated: false,
  orgId: null,
  orgRole: null,
  orgSlug: null,
  user: null,
  login: null,
  logout: null,
  getToken: null,
  has: null,
});

const Auth0ContextProvider = ({ children }) => {
  const { isLoading, isAuthenticated, user, loginWithRedirect, logout, getAccessTokenSilently } =
    useAuth0();

  const value = useMemo(
    () => ({
      provider: AUTH_PROVIDERS.AUTH0,
      isLoading,
      isAuthenticated,
      orgId: null,
      orgRole: null,
      orgSlug: null,
      user: normalizeAuth0User(user),
      login: loginWithRedirect,
      logout,
      getToken: () => getAccessTokenSilently(),
      has: null,
    }),
    [getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, logout, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const ClerkContextProvider = ({ children }) => {
  const { isLoaded, isSignedIn, getToken, signOut, orgId, orgRole, orgSlug, has } = useAuth();
  const { user } = useUser();
  const clerkJwtTemplate = getConfigValue(
    "REACT_APP_CLERK_JWT_TEMPLATE",
    process.env.REACT_APP_CLERK_JWT_TEMPLATE,
    ""
  );

  const value = useMemo(
    () => ({
      provider: AUTH_PROVIDERS.CLERK,
      isLoading: !isLoaded,
      isAuthenticated: Boolean(isSignedIn),
      orgId: orgId || null,
      orgRole: orgRole || null,
      orgSlug: orgSlug || null,
      user: normalizeClerkUser(user),
      login: null,
      logout: () => signOut(),
      getToken: (options = {}) =>
        getToken(clerkJwtTemplate ? { template: clerkJwtTemplate, ...options } : options),
      has,
    }),
    [clerkJwtTemplate, getToken, has, isLoaded, isSignedIn, orgId, orgRole, orgSlug, signOut, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AuthProviderSwitch = ({ children }) => {
  const provider = getAuthProvider();

  if (provider === AUTH_PROVIDERS.CLERK) {
    return (
      <ClerkAuthProvider>
        <ClerkContextProvider>{children}</ClerkContextProvider>
      </ClerkAuthProvider>
    );
  }

  return (
    <Auth0Provider
      domain={getConfigValue("REACT_APP_AUTH0_DOMAIN", process.env.REACT_APP_AUTH0_DOMAIN)}
      clientId={getConfigValue("REACT_APP_AUTH0_CLIENT_ID", process.env.REACT_APP_AUTH0_CLIENT_ID)}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: getConfigValue("REACT_APP_AUTH0_AUDIENCE", process.env.REACT_APP_AUTH0_AUDIENCE),
      }}
      cacheLocation="localstorage"
    >
      <Auth0ContextProvider>{children}</Auth0ContextProvider>
    </Auth0Provider>
  );
};

export const useAuthProvider = () => useContext(AuthContext);

export default AuthProviderSwitch;
